import React, { useEffect } from "react";
import { Chord, NoteDuration } from "../../model/core";
import { ChordAliasPartition } from "../../utils/ChordAliasPartition";
import {
    convertStartToCSSOffset,
    convertToCSSWidth,
    getChordSynesthesiaColor,
    normalizeClassName,
    NOTE_WIDTH
} from "../../render/renderUtils";
import { ChordInputFloatingDialog } from "../dialogs/ChordInputFloatingDialog";
import { ApiFactory } from "../../ApiClient";
import './ChordCollapsed.css';

const API = ApiFactory.create();

export function ChordCollapsed({
    chord,
    start,
    updateChordCB,
    updateChordDurationCB,
    ppq,
    zoomFactor
}: {
    chord: { payload: Chord, duration: NoteDuration },
    start: number,
    updateChordCB?: (chord: Chord) => void,
    updateChordDurationCB?: (start: number, duration: NoteDuration) => void,
    ppq: number,
    zoomFactor: number
}) {

    const [chordAliasCount, setChordAliasCount] = React.useState(0);
    const [chordSelectorRefEl, setChordSelectorRefEl] = React.useState<EventTarget & HTMLElement>();
    const width = convertToCSSWidth(chord.duration, zoomFactor);

    const style = {
        width: width,
        left: convertStartToCSSOffset(start, zoomFactor),
        height: 'calc(var(--note-width))',
        overflow: 'visible',
        borderTop: chord ? `var(--collapsed-chord-synesthesia-color-height) solid var(${getChordSynesthesiaColor(chord.payload)})` : ''
    };

    const classes = ["piano-roll-note", "piano-roll-note-active", "chord-roll--collapsed"];
    const chordName = chord ? (chord.payload.root.getName() + ' ' + chord.payload.family) : '';
    let startX = 0;

    useEffect(() => {
        // FIXME: check if we need the first get chord call.
        API.getChord(new Chord(chord.payload.root, chord.payload.family))
            .then((c: Chord) => {

                // FIXME: when loading the project from a file, we may have incomplete chord information at this point.
                // If we are in this situation, we update the parent with the full chord and rerender this component.
                // if (!chord.payload.tones || chord.payload.tones.length === 0) {
                //     updateChordCB && updateChordCB(c);
                //     return;
                // }

                API.getChordIdByNotes(c.tones.map(t => t.index)).then((data) => {
                    setChordAliasCount(ChordAliasPartition.get(c)?.length || 0);
                })
            });
    }, [chord]);

    return (<>
        <div className={normalizeClassName(classes)}
            style={style}
            onClick={(e) => setChordSelectorRefEl(e.currentTarget)}>
            <div style={{
                width: width,
                overflow: 'hidden',
            }}>{chordName ? chordName : 'Empty'}
                {chordAliasCount > 1 &&
                    <span className="chord-roll--chord-alternate-names--indicator">(+{chordAliasCount - 1} aliases)</span>
                }
            </div>

            <div className="piano-roll--note-duration-handle"
                style={{
                    height: 'calc(var(--note-width) - var(--collapsed-chord-synesthesia-color-height))'
                }}
                draggable="true"
                onDragStart={(e: { clientX: number, dataTransfer: any }) => {
                    startX = e.clientX;
                }}
                onDragEnd={(e) => {
                    if (updateChordDurationCB) {
                        const delta = (e.clientX - startX) * zoomFactor / NOTE_WIDTH;
                        const d = new NoteDuration(0 | (chord.duration.value + delta));
                        updateChordDurationCB(start, d);
                    }
                }} />
        </div>
        {chordSelectorRefEl &&
            <ChordInputFloatingDialog
                chordSelectorRefEl={chordSelectorRefEl}
                updateChordCB={updateChordCB}
                chord={chord.payload}
                setChordSelectorRefEl={setChordSelectorRefEl}
            />
        }
    </>
    );
}